import { forwardRef, useLayoutEffect, useRef, useState } from "react";
import { getDecodedHtml } from "utils/getDecodedHtml";
import {
	isContentMax,
	isDesktopRange,
	isTablet,
	isMobile
} from "components/utility/page-responsive/PageResponsive";
import { Tile, TileRowProps } from "./types/ddiff-research-is-in-our-DNA-types";

const DDiffResearchIsInOurDNATileRow = forwardRef<HTMLDivElement, TileRowProps>(
	({ updatedBreakpoint, tiles }, ref) => {
		const [tileHeadingHeight, setTileHeadingHeight] = useState<string>("");
		const [grayTileSkewAngle, setGrayTileSkewAngle] = useState<number | null>(0);
		const [controllerElementId, setcontrollerElementId] = useState<string>("");
		const tileRefs = useRef<HTMLDivElement[]>([]);
		const tileRowRef = useRef<HTMLDivElement | null>(null);

		useLayoutEffect(() => {
			const getTileMeasurements = (elements: Array<HTMLDivElement>): number => {
				let tallestHeadingValue: number = 0;
				let highestCharCount: number = 0;

				elements?.forEach((currEl: HTMLDivElement, index: number) => {
					let currentElHeadingHeight: number = 0;
					let currentElHeadingCharCount: number = 0;
					const currentTileId = currEl.dataset.tileId || "";
					const curTileHeading: HTMLElement | null = currEl.querySelector(".tile-heading");
					if (curTileHeading) {
						currentElHeadingHeight = curTileHeading.getBoundingClientRect().height || 0;
						currentElHeadingCharCount = curTileHeading.textContent?.length || 0;
						if (index === 0) {
							tallestHeadingValue = currentElHeadingHeight;
							highestCharCount = currentElHeadingCharCount;
							if (currEl) {
								setcontrollerElementId(currentTileId);
							}
						} else {
							if (currentElHeadingCharCount) {
								if (currentElHeadingCharCount > highestCharCount) {
									setcontrollerElementId(currentTileId);
									tallestHeadingValue = currentElHeadingHeight;
								}
							}
						}
					}
				});
				return tallestHeadingValue;
			};

			const calculateTileSkewAngle = (width: number, height: number, gapValue: string) => {
				const numericGapValue = parseInt(gapValue.replace(/[^0-9]/g, ""));
				return Math.atan((width + numericGapValue) / height);
			};

			const setTilesDimensions = (parentElement: HTMLElement) => {
				if (tileRefs.current.length) {
					// On Resize, find tallest tile header and tile content blocks, adjust text elements to match tallest els.
					setTileHeadingHeight(getTileMeasurements(tileRefs.current) + "px");

					// Then take the width and height of a tile and find the skew angle
					const tileCSSObj = parentElement ? window.getComputedStyle(parentElement) : null;
					if (tileCSSObj) {
						const rowColGap = tileCSSObj.getPropertyValue("column-gap");
						const grayTileSkewAngle = calculateTileSkewAngle(
							tileRefs.current[0].getBoundingClientRect().width,
							tileRefs.current[0].getBoundingClientRect().height,
							rowColGap
						);
						setGrayTileSkewAngle(grayTileSkewAngle);
					}
				}
			};

			const tilesHeightObserver = new ResizeObserver((entry) => {
				const entryTarget = entry[0].target;
				const entryParent = entryTarget.parentElement;
				if (entryParent) {
					setTilesDimensions(entryParent);
				}
			});

			const initializeTileHeightObservations = () => {
				if (tileRefs.current.length) {
					tileRefs.current.forEach((element) => {
						if (element) {
							tilesHeightObserver.observe(element);
						}
					});
				}
			};
			initializeTileHeightObservations();

			if (tileRowRef.current) {
				if (isContentMax() || isDesktopRange() || isTablet()) {
					setTilesDimensions(tileRowRef.current);
				}

				if (updatedBreakpoint === "contentMax" || updatedBreakpoint === "medium") {
					setTilesDimensions(tileRowRef.current);
				}
			}

			return () => {
				tilesHeightObserver.disconnect();
			};
		});

		return (
			<div className="tile-row-intersection ddiff-research-md-animation" ref={ref}>
				<div className="tile-row" ref={tileRowRef}>
					{tiles?.map((tile: Tile) => (
						<div
							className="tile"
							ref={(el: HTMLDivElement) => {
								if (el && !tileRefs.current.includes(el)) {
									tileRefs.current.push(el);
								}
							}}
							key={"tile-" + tile.Id}
							data-tile-id={tile.Id}
						>
							<div
								className="gray-tile"
								style={{ transform: `skew(${grayTileSkewAngle + "rad"})` }}
							></div>
							<div className="color-tile"></div>
							<div className="tile-content">
								<div
									className="tile-heading t-heading-lg tc-white"
									style={
										isMobile() || updatedBreakpoint === "mobile"
											? { height: "auto" }
											: controllerElementId !== tile.Id
												? { height: tileHeadingHeight }
												: { height: "auto" }
									}
									dangerouslySetInnerHTML={{ __html: getDecodedHtml(tile.tileTitleText) }}
								/>
								<div
									className="tile-description t-body-sm tc-white"
									dangerouslySetInnerHTML={{ __html: getDecodedHtml(tile.tileDescriptionText) }}
								/>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}
);
DDiffResearchIsInOurDNATileRow.displayName = "DDiffResearchIsInOurDNATileRow";

export default DDiffResearchIsInOurDNATileRow;
